import { getAnchorIdFromTitle } from "@/helpers/getAnchorIdFromTitle";
import cn from "classnames";
import { FC, useMemo } from "react";
import { Text, TextType } from "../Text";
import { Description } from "./Description";

export type TextCombinationType = 'd1p1' | 'd2p1' | 'd3p1' | 'h1p1' | 'h1p2' | 'h2p2' | 'h3p2' | 'p2' | 'p1';

export type TextCombinationProps = {
  type: TextCombinationType;
  align: 'left' | 'center';
  eyebrow?: string;
  title: string;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
  isHeadingBalanced?: boolean;
  description?: string | React.ReactNode;
  className?: string;
}

const TEXT_COMBINATION_VARIANTS = {
  'd1p1': {
    title: 'display1',
    description: 'p1',
  },
  'd2p1': {
    title: 'display2',
    description: 'p1',
  },
  'd3p1': {
    title: 'display3',
    description: 'p1',
  },
  'h1p1': {
    title: 'h1',
    description: 'p1',
  },
  'h1p2': {
    title: 'h1',
    description: 'p2',
  },
  'h2p2': {
    title: 'h2',
    description: 'p2',
  },
  'h3p2': {
    title: 'h3',
    description: 'p2',
  },
  'p2': {
    title: 'p2-medium',
    description: 'p2',
  },
  'p1': {
    title: 'p1-medium',
    description: 'p1',
  },
} as const satisfies Record<TextCombinationType, { title: TextType; description: TextType }>;

export const TextCombination: FC<TextCombinationProps> = ({
  type = 'd1p1',
  align = 'left',
  eyebrow,
  title,
  headingLevel = 'h2',
  isHeadingBalanced = true,
  description,
  className
}) => {
  const { title: titleType, description: descriptionType } = TEXT_COMBINATION_VARIANTS[type];
  const textAlignClass = useMemo(() => align === 'center' ? 'text-center' : 'text-left', [align]);
  const titleId = useMemo(() => getAnchorIdFromTitle(title), [title]);

  const descriptionClassName = useMemo(() => cn('text-secondary text-pretty', {
    // Description text has different max-widths for center and left alignment
    'max-w-dimensions-840 mx-auto': align === 'center',
    'max-w-dimensions-732': align === 'left',
    // Description has tighter top spacing specifically for h3p2 type.
    'mt-xs': type !== 'h3p2',
    'mt-2xs': type === 'h3p2',
    'mt-4xs': type === 'p2'
  }), [description]);

  return (
    <div className={cn(className, textAlignClass)}>
      {eyebrow && <Text type='micro1' className="text-secondary mb-xs">{eyebrow}</Text>}
      <Text id={titleId} as={headingLevel} type={titleType} className={cn("text-primary lg:whitespace-pre-wrap", isHeadingBalanced ? 'text-balance' : '')}>{title}</Text>
      <Description description={description} descriptionType={descriptionType} descriptionClassName={descriptionClassName} />
    </div>
  );
};
